/** Individual symbol sprite on a reel */

import { Container, Sprite, Texture, Graphics, Text, TextStyle } from 'pixi.js';
import { Spine } from '@esotericsoftware/spine-pixi-v8';
import { GameConfig } from '../config/GameConfig';
import { SpineManager, SYMBOL_WIN_ANIMS, SYMBOL_HIT_ANIMS } from '../animations/SpineManager';
import { Logger } from '../utils/Logger';

const logger = new Logger('ReelSymbol');

/** Map of loaded textures keyed by symbol ID */
const textureCache: Map<string, Texture> = new Map();

/** Register a loaded texture for a symbol */
export function registerSymbolTexture(symbolId: string, texture: Texture): void {
  textureCache.set(symbolId, texture);
}

/** A single symbol tile on a reel */
export class ReelSymbol extends Container {
  private _sprite: Sprite | null = null;
  private _placeholder: Container | null = null;
  private _spineInstance: Spine | null = null;
  private _spineSkeletonName: string | null = null;
  private _symbolId: string = '';
  private _width: number;
  private _height: number;
  private _cardBg: Graphics;

  constructor(width: number = GameConfig.SYMBOL_WIDTH, height: number = GameConfig.SYMBOL_HEIGHT) {
    super();
    this._width = width;
    this._height = height;

    // FIX A: Draw a dark blue "card slot" background behind the symbol
    this._cardBg = new Graphics();
    const hw = this._width / 2;
    const hh = this._height / 2;
    // Bottom darker blue layer (full size)
    this._cardBg.roundRect(-hw, -hh, this._width, this._height, 8);
    this._cardBg.fill({ color: 0x061230, alpha: 0.8 });
    // Top slightly lighter blue layer (inset 2px) — subtle border effect
    this._cardBg.roundRect(-hw + 2, -hh + 2, this._width - 4, this._height - 4, 6);
    this._cardBg.fill({ color: 0x0c1e4a, alpha: 0.8 });
    // Subtle top highlight strip
    this._cardBg.roundRect(-hw + 2, -hh + 2, this._width - 4, 2, 1);
    this._cardBg.fill({ color: 0x1a3a6a, alpha: 0.3 });
    this.addChild(this._cardBg);
  }

  get symbolId(): string {
    return this._symbolId;
  }

  /** Set the symbol to display */
  setSymbol(symbolId: string): void {
    if (this._symbolId === symbolId) return;
    this._symbolId = symbolId;

    const texture = textureCache.get(symbolId);
    if (texture) {
      // Reuse existing sprite if possible -- avoids GC churn during spinning
      if (this._sprite) {
        this._sprite.texture = texture;
        this._fitSpriteToCell(this._sprite, texture);
        this._sprite.visible = true;
      } else {
        this.clearDisplay();
        this._sprite = new Sprite(texture);
        this._sprite.anchor.set(0.5, 0.5);
        this._fitSpriteToCell(this._sprite, texture);
        this.addChild(this._sprite);
      }
      // Hide placeholder if it existed from a previous symbol
      if (this._placeholder) {
        this._placeholder.visible = false;
      }
    } else {
      logger.warn(`Texture not loaded for symbol: ${symbolId}`);
      if (this._sprite) {
        this._sprite.visible = false;
      }
      this.clearPlaceholder();
      this.createFallbackPlaceholder(symbolId);
    }
  }

  /** Scale sprite to fit cell while preserving aspect ratio */
  private _fitSpriteToCell(sprite: Sprite, texture: Texture): void {
    // Reset scale to get natural dimensions
    sprite.scale.set(1, 1);
    const texW = texture.width;
    const texH = texture.height;
    const scale = Math.min(this._width / texW, this._height / texH);
    sprite.width = texW * scale;
    sprite.height = texH * scale;
  }

  /** Dim the symbol (when not part of a winning combination) */
  dim(alpha: number = 0.3): void {
    this.alpha = alpha;
  }

  /** Restore full brightness */
  undim(): void {
    this.alpha = 1;
  }

  /**
   * Play win animation using Spine skeleton.
   * Hides the static sprite, overlays a Spine instance with the win animation.
   * Falls back gracefully if skeleton is not loaded.
   */
  async playWinAnimation(): Promise<void> {
    const mapping = SYMBOL_WIN_ANIMS[this._symbolId];
    if (!mapping) return;

    const spineManager = SpineManager.getInstance();
    if (!spineManager.isLoaded(mapping.skeleton)) {
      logger.debug(`Spine skeleton not loaded for win anim: ${mapping.skeleton}`);
      return;
    }

    // Clean up any previous Spine instance
    this._cleanupSpine();

    const instance = spineManager.createInstance(mapping.skeleton);
    if (!instance) return;

    this._spineInstance = instance;
    this._spineSkeletonName = mapping.skeleton;

    // Size the Spine instance to fit the symbol cell
    instance.scale.set(
      this._width / (instance.width || this._width),
      this._height / (instance.height || this._height),
    );

    // Hide static display
    if (this._sprite) this._sprite.visible = false;
    if (this._placeholder) this._placeholder.visible = false;

    this.addChild(instance);

    // Play the win animation (one-shot)
    await spineManager.playAnimation(instance, mapping.animation, false);
  }

  /**
   * Play hit (land) animation if available, otherwise no-op.
   */
  async playHitAnimation(): Promise<void> {
    const mapping = SYMBOL_HIT_ANIMS[this._symbolId];
    if (!mapping) return;

    const spineManager = SpineManager.getInstance();
    if (!spineManager.isLoaded(mapping.skeleton)) return;

    this._cleanupSpine();

    const instance = spineManager.createInstance(mapping.skeleton);
    if (!instance) return;

    this._spineInstance = instance;
    this._spineSkeletonName = mapping.skeleton;

    instance.scale.set(
      this._width / (instance.width || this._width),
      this._height / (instance.height || this._height),
    );

    if (this._sprite) this._sprite.visible = false;
    if (this._placeholder) this._placeholder.visible = false;

    this.addChild(instance);

    await spineManager.playAnimation(instance, mapping.animation, false);

    // After hit animation completes, return to static display
    this.stopWinAnimation();
  }

  /** Stop win animation, return Spine instance to pool, show static sprite */
  stopWinAnimation(): void {
    this._cleanupSpine();

    // Restore static display
    if (this._sprite) this._sprite.visible = true;
    if (this._placeholder) this._placeholder.visible = true;
  }

  /** Internal cleanup of the Spine overlay */
  private _cleanupSpine(): void {
    if (this._spineInstance && this._spineSkeletonName) {
      const spineManager = SpineManager.getInstance();
      spineManager.returnInstance(this._spineSkeletonName, this._spineInstance);
      this._spineInstance = null;
      this._spineSkeletonName = null;
    }
  }

  private clearDisplay(): void {
    if (this._sprite) {
      this.removeChild(this._sprite);
      this._sprite.destroy();
      this._sprite = null;
    }
    this.clearPlaceholder();
  }

  private clearPlaceholder(): void {
    if (this._placeholder) {
      this.removeChild(this._placeholder);
      this._placeholder.destroy({ children: true });
      this._placeholder = null;
    }
  }

  private createFallbackPlaceholder(symbolId: string): void {
    const container = new Container();

    const bg = new Graphics();
    bg.roundRect(-this._width / 2, -this._height / 2, this._width, this._height, 8);
    bg.fill({ color: 0x333366, alpha: 0.8 });
    container.addChild(bg);

    const style = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 20,
      fill: 0xffffff,
    });
    const text = new Text({ text: symbolId, style });
    text.anchor.set(0.5, 0.5);
    container.addChild(text);

    this._placeholder = container;
    this.addChild(container);
  }
}
